import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { Box, Button, Typography } from '@mui/material';
import { FC, memo, useMemo } from 'react';
import {
  ProductCourseDay,
  ProductCourseGroup,
  ProductCourseSection,
  ProductCoursesResponse,
} from 'src/entities/ProductCourse.entity';

import style from './print.module.css';

// A4 縦 1 枚に収まる明細行数の上限。font-size 14px / line-height 1.3 の行高は
// フォント非依存で 1 行 ≒ 19px、ページヘッダ・thead を差し引くと 49 行が物理上限
// (余白 0.8mm)、50 は溢れて余計なページが出ていた。ヘッダのフォント差・プリンタの
// ハード余白を吸収するため余白約 16mm を確保できる 46 に抑える(倍率100%で実測)。
export const ROWS_PER_PAGE = 46;

type Props = {
  data: ProductCoursesResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  hasMissingParams?: boolean;
  issueDate: string;
  issueTime: string;
};

type FlatRow =
  | { kind: 'group'; group: ProductCourseGroup }
  | { kind: 'line'; group: ProductCourseGroup; lineIndex: number };

type RawPage = {
  day: ProductCourseDay;
  section: ProductCourseSection;
  rows: FlatRow[];
};

export type PageInfo = RawPage & { pageInSection: number; totalInSection: number };

function buildGroupRows(group: ProductCourseGroup): FlatRow[] {
  return [
    { kind: 'group', group },
    ...group.lines.map<FlatRow>((_, i) => ({ kind: 'line', group, lineIndex: i })),
  ];
}

export function buildPages(days: ProductCourseDay[]): RawPage[] {
  const pages: RawPage[] = [];
  days.forEach((day) => {
    day.sections.forEach((section) => {
      if (section.groups.length === 0) return;
      let currentRows: FlatRow[] = [];
      const flushPage = () => {
        if (currentRows.length === 0) return;
        pages.push({ day, section, rows: currentRows });
        currentRows = [];
      };
      section.groups.forEach((group) => {
        const groupRows = buildGroupRows(group);
        if (groupRows.length > ROWS_PER_PAGE) {
          // 商品グループ単独で 1 ページに収まらない稀なケース。現在ページを締め、
          // 各継続ページ先頭に商品CD/商品名(グループ行)を再掲して明細を分割する
          // (明細だけで商品情報の無いページを作らないため)。
          flushPage();
          const [groupHeader, ...lineRows] = groupRows;
          for (let i = 0; i < lineRows.length; i += ROWS_PER_PAGE - 1) {
            currentRows = [groupHeader, ...lineRows.slice(i, i + ROWS_PER_PAGE - 1)];
            flushPage();
          }
          return;
        }
        if (currentRows.length + groupRows.length > ROWS_PER_PAGE) {
          flushPage();
        }
        currentRows.push(...groupRows);
      });
      flushPage();
    });
  });
  return pages;
}

// 和式区切り（全角空白 U+3000）は eslint no-irregular-whitespace を避けるため Unicode escape を使う
const IDEOGRAPHIC_SPACE = '\u3000';

export function formatJapaneseDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${y}年${m}月${d}日${IDEOGRAPHIC_SPACE}配送`;
}

function withPageNumbers(pages: RawPage[]): PageInfo[] {
  const totalByKey = new Map<string, number>();
  pages.forEach((p) => {
    const key = `${p.day.delivery_date}__${p.section.shipper_name}`;
    totalByKey.set(key, (totalByKey.get(key) ?? 0) + 1);
  });
  const indexByKey = new Map<string, number>();
  return pages.map((p) => {
    const key = `${p.day.delivery_date}__${p.section.shipper_name}`;
    const idx = (indexByKey.get(key) ?? 0) + 1;
    indexByKey.set(key, idx);
    return { ...p, pageInSection: idx, totalInSection: totalByKey.get(key) ?? 1 };
  });
}

// 回転ごとに番号を持つ行 (rotation_numbered) は「コース番号-回転」を付けない (D2 ルール 8。番号そのものが回転の番号)
function formatCourseLabel(line: { course_number: string | null; rotation: number; rotation_numbered: boolean }, showRotation: boolean): string {
  if (line.course_number == null) return '';
  if (showRotation && !line.rotation_numbered && line.rotation >= 2) return `${line.course_number}-${line.rotation}`;
  return line.course_number;
}

const ProductCourseReportPresenter: FC<Props> = memo(({ data, isLoading, isError, hasMissingParams = false, issueDate, issueTime }) => {
  const rawPages = useMemo(() => buildPages(data?.days ?? []), [data]);
  const pages = useMemo(() => withPageNumbers(rawPages), [rawPages]);
  const totalAll = Math.max(pages.length, 1);

  return (
    <>
      <title>商品別コースリスト</title>
      <div className={style.shell} role="region" aria-label="商品別コースリスト プレビュー">
        <div className={style.toolbar}>
          <Button variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => window.close()}>
            戻る
          </Button>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            商品別コースリスト
          </Typography>
          <Box className={style.toolbarRight}>
            <Typography variant="caption" color="text.secondary">
              全 {totalAll} ページ
            </Typography>
            <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={() => window.print()}>
              印刷
            </Button>
          </Box>
        </div>

        <div className={style.body}>
          {hasMissingParams && (
            <div className={style.empty}>印刷データのパラメータが不足しています</div>
          )}
          {!hasMissingParams && isLoading && <div className={style.empty}>読み込み中...</div>}
          {!hasMissingParams && isError && <div className={style.empty}>データの取得に失敗しました。</div>}
          {!hasMissingParams && !isLoading && !isError && pages.length === 0 && (
            <div className={style.empty}>該当するデータはありません。</div>
          )}
          {pages.map((page) => (
            <div
              className={style.page}
              style={{ page: 'A4ProductCourse' }}
              key={`${page.day.delivery_date}-${page.section.shipper_name}-${page.pageInSection}`}
            >
              <div className={style.header}>
                <div className={style.title}>商品別コースリスト</div>
                <div className={style.sub}>
                  {page.section.shipper_name}{IDEOGRAPHIC_SPACE}{formatJapaneseDate(page.day.delivery_date)}
                </div>
                <div className={style.meta}>
                  発行日付: {issueDate}<br />
                  発行時刻: {issueTime}<br />
                  ページ: {page.pageInSection} / {page.totalInSection}
                </div>
              </div>
              <table className={style.table}>
                <thead>
                  <tr>
                    <th style={{ width: '18%' }}>商品CD<br />/ コース</th>
                    <th style={{ width: '28%' }}>商品名<br />/ 担当者</th>
                    <th style={{ width: '7%' }}>数量</th>
                    <th style={{ width: '12%' }}>地区</th>
                    <th style={{ width: '25%' }}>納品先名</th>
                    <th style={{ width: '10%' }}>配送件数</th>
                  </tr>
                </thead>
                <tbody>
                  {page.rows.map((row, i) => {
                    if (row.kind === 'group') {
                      const g = row.group;
                      return (
                        <tr key={i} className={style.groupRow}>
                          <td className={style.center}>
                            <strong>{g.product_code}</strong>
                          </td>
                          <td>
                            <strong>{g.product_name}</strong>
                          </td>
                          <td className={style.num}>
                            <strong>{g.total_quantity}</strong>
                          </td>
                          <td colSpan={2} />
                          <td className={style.center}>
                            <strong>[ {g.delivery_count} 件 ]</strong>
                          </td>
                        </tr>
                      );
                    }
                    const line = row.group.lines[row.lineIndex];
                    return (
                      <tr key={i}>
                        <td className={style.num}>{formatCourseLabel(line, page.section.show_rotation)}</td>
                        <td>{line.driver_name}</td>
                        <td className={style.num}>{line.quantity}</td>
                        <td className={style.areaCell}>{line.area}</td>
                        {/* 明細行は配送件数欄を使わないため、納品先名で 2 列分を埋めて文字切れを軽減する */}
                        <td colSpan={2}>{line.store_name}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </>
  );
});

export default ProductCourseReportPresenter;
